// Liest E-Rechnungen im Browser: XRechnung/Peppol (UBL), ZUGFeRD/Factur-X (CII, auch eingebettet im PDF)
// und das knappe EFactuur-Format. Nichts verlässt den Browser.

export type Steuergruppe = { satz: number; basis: number; steuer: number }; // Cent

export type Partei = {
  name: string;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  land: string | null;
  email: string | null;
  telefon: string | null;
  ustId: string | null;
  kontakt: string | null;
};

const LEER: Partei = { name: "", strasse: null, plz: null, ort: null, land: null, email: null, telefon: null, ustId: null, kontakt: null };

export type ERechnung = {
  format: "UBL" | "CII" | "EFactuur";
  nummer: string | null;
  datum: string | null;
  brutto: number | null; // Cent
  verkaeufer: string;
  kaeufer: string;
  lieferant: Partei;
  kunde: Partei;
  gruppen: Steuergruppe[];
  satz: number | null;
  bruttoErmaessigt: number | null; // Cent, nur bei 7/19-Mischrechnung
  gutschrift: boolean;
  hinweise: string[];
};

const SAETZE = [0, 7, 19];

const kind = (
  el: Element | null | undefined,
  ...pfad: string[]
): Element | null => {
  let jetzt: Element | null = el ?? null;
  for (const name of pfad) {
    if (!jetzt) return null;
    jetzt =
      Array.from(jetzt.children).find((c) => c.localName === name) ?? null;
  }
  return jetzt;
};
const kinder = (el: Element | null, name: string) =>
  el ? Array.from(el.children).filter((c) => c.localName === name) : [];
const text = (el: Element | null) => el?.textContent?.trim() || null;
const cents = (el: Element | null) => {
  const t = text(el);
  if (t === null) return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const isoDatum = (t: string | null) => {
  if (!t) return null;
  const m = t.match(/^(\d{4})-?(\d{2})-?(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

// Der Satz wird aus dem Steuerbetrag errechnet; manche Programme schreiben einen falschen Prozentwert.
const satzAusBetrag = (basis: number, steuer: number) => {
  if (basis <= 0) return steuer === 0 ? 0 : null;
  const roh = (steuer / basis) * 100;
  return SAETZE.find((s) => Math.abs(s - roh) < 0.3) ?? null;
};

const gruppenPruefen = (
  roh: { satz: number | null; basis: number; steuer: number }[],
  hinweise: string[],
) =>
  roh.map((g) => {
    const errechnet = satzAusBetrag(g.basis, g.steuer);
    if (errechnet !== null && g.satz !== null && errechnet !== g.satz) {
      hinweise.push(
        `Datei nennt ${g.satz} %, der Steuerbetrag ergibt ${errechnet} %`,
      );
    }
    if (errechnet === null)
      hinweise.push("Steuersatz passt zu keinem deutschen Satz, bitte prüfen");
    return { satz: errechnet ?? g.satz ?? 0, basis: g.basis, steuer: g.steuer };
  });

const abschliessen = (
  r: Omit<ERechnung, "satz" | "bruttoErmaessigt">,
): ERechnung => {
  const saetze = [...new Set(r.gruppen.map((g) => g.satz))];
  const nurMisch =
    saetze.length === 2 && saetze.includes(7) && saetze.includes(19);
  if (saetze.length > 1 && !nurMisch)
    r.hinweise.push("Mehrere Steuersätze, bitte aufteilen");
  const sieben = r.gruppen
    .filter((g) => g.satz === 7)
    .reduce((s, g) => s + g.basis + g.steuer, 0);
  return {
    ...r,
    satz: saetze.length === 1 ? saetze[0] : nurMisch ? 19 : null,
    bruttoErmaessigt: nurMisch ? sieben : null,
  };
};

const ubl = (wurzel: Element): ERechnung => {
  const hinweise: string[] = [];
  const partei = (rolle: string) => {
    const p = kind(wurzel, rolle, "Party");
    return (
      text(kind(p, "PartyLegalEntity", "RegistrationName")) ??
      text(kind(p, "PartyName", "Name")) ??
      ""
    );
  };
  const parteiVoll = (rolle: string): Partei => {
    const p = kind(wurzel, rolle, "Party");
    const adresse = kind(p, "PostalAddress");
    const land = text(kind(adresse, "Country", "IdentificationCode"));
    return {
      name: partei(rolle),
      strasse: [text(kind(adresse, "StreetName")), text(kind(adresse, "BuildingNumber"))].filter(Boolean).join(" ") || null,
      plz: text(kind(adresse, "PostalZone")),
      ort: text(kind(adresse, "CityName")),
      land: land && /^[A-Z]{2}$/.test(land) ? land : null,
      email: text(kind(p, "Contact", "ElectronicMail")),
      telefon: text(kind(p, "Contact", "Telephone")),
      ustId: kinder(p, "PartyTaxScheme").map((t) => text(kind(t, "CompanyID"))).find((id) => id && /^[A-Z]{2}[0-9A-Z]{8,12}$/.test(id)) ?? null,
      kontakt: text(kind(p, "Contact", "Name")),
    };
  };
  const summen = kind(wurzel, "LegalMonetaryTotal");
  const brutto =
    cents(kind(summen, "TaxInclusiveAmount")) ??
    cents(kind(summen, "PayableAmount"));
  const steuerSumme =
    kinder(wurzel, "TaxTotal").find((t) => kind(t, "TaxAmount")) ?? null;
  const teile = kinder(steuerSumme, "TaxSubtotal").map((s) => ({
    satz: cents(kind(s, "TaxCategory", "Percent")),
    basis: cents(kind(s, "TaxableAmount")) ?? 0,
    steuer: cents(kind(s, "TaxAmount")) ?? 0,
  }));
  let roh = teile.map((t) => ({
    ...t,
    satz: t.satz === null ? null : t.satz / 100,
  }));
  // Ohne brauchbare Teilsummen aus Gesamtsteuer und Brutto rechnen
  if (!roh.length || roh.every((t) => t.basis === 0)) {
    const steuer = cents(kind(steuerSumme, "TaxAmount"));
    const angegeben = kind(
      kinder(wurzel, "TaxTotal")[0],
      "TaxSubtotal",
      "TaxCategory",
      "Percent",
    );
    roh =
      brutto !== null && steuer !== null
        ? [
            {
              satz: angegeben ? Number(text(angegeben)) : null,
              basis: brutto - steuer,
              steuer,
            },
          ]
        : [];
  }
  // Netto falsch angegeben (z. B. gleich Brutto): aus Brutto minus Steuer neu rechnen
  if (roh.length === 1 && brutto !== null && Math.abs(roh[0].basis + roh[0].steuer - brutto) > 1) {
    hinweise.push("Nettobetrag in der Datei passt nicht zu Brutto und Steuer, aus Brutto neu gerechnet");
    roh = [{ ...roh[0], basis: brutto - roh[0].steuer }];
  }
  const typ = text(kind(wurzel, "InvoiceTypeCode"));
  return abschliessen({
    format: "UBL",
    nummer: text(kind(wurzel, "ID")),
    datum: isoDatum(text(kind(wurzel, "IssueDate"))),
    brutto,
    verkaeufer: partei("AccountingSupplierParty"),
    kaeufer: partei("AccountingCustomerParty"),
    lieferant: parteiVoll("AccountingSupplierParty"),
    kunde: parteiVoll("AccountingCustomerParty"),
    gruppen: gruppenPruefen(roh, hinweise),
    gutschrift: wurzel.localName === "CreditNote" || typ === "381",
    hinweise,
  });
};

const cii = (wurzel: Element): ERechnung => {
  const hinweise: string[] = [];
  const dok = kind(wurzel, "ExchangedDocument");
  const handel = kind(wurzel, "SupplyChainTradeTransaction");
  const vereinbarung = kind(handel, "ApplicableHeaderTradeAgreement");
  const abrechnung = kind(handel, "ApplicableHeaderTradeSettlement");
  const summen = kind(
    abrechnung,
    "SpecifiedTradeSettlementHeaderMonetarySummation",
  );
  const parteiVoll = (rolle: string): Partei => {
    const p = kind(vereinbarung, rolle);
    const adresse = kind(p, "PostalTradeAddress");
    const kontakt = kind(p, "DefinedTradeContact");
    return {
      name: text(kind(p, "Name")) ?? "",
      strasse: text(kind(adresse, "LineOne")),
      plz: text(kind(adresse, "PostcodeCode")),
      ort: text(kind(adresse, "CityName")),
      land: text(kind(adresse, "CountryID")),
      email: text(kind(kontakt, "EmailURIUniversalCommunication", "URIID")) ?? text(kind(p, "URIUniversalCommunication", "URIID")),
      telefon: text(kind(kontakt, "TelephoneUniversalCommunication", "CompleteNumber")),
      ustId: kinder(p, "SpecifiedTaxRegistration").map((t) => kind(t, "ID")).find((id) => id?.getAttribute("schemeID") === "VA")?.textContent?.trim() ?? null,
      kontakt: text(kind(kontakt, "PersonName")),
    };
  };
  const roh = kinder(abrechnung, "ApplicableTradeTax").map((t) => ({
    satz:
      text(kind(t, "RateApplicablePercent")) === null
        ? null
        : Number(text(kind(t, "RateApplicablePercent"))),
    basis: cents(kind(t, "BasisAmount")) ?? 0,
    steuer: cents(kind(t, "CalculatedAmount")) ?? 0,
  }));
  return abschliessen({
    format: "CII",
    nummer: text(kind(dok, "ID")),
    datum: isoDatum(text(kind(dok, "IssueDateTime", "DateTimeString"))),
    brutto:
      cents(kind(summen, "GrandTotalAmount")) ??
      cents(kind(summen, "DuePayableAmount")),
    verkaeufer: text(kind(vereinbarung, "SellerTradeParty", "Name")) ?? "",
    kaeufer: text(kind(vereinbarung, "BuyerTradeParty", "Name")) ?? "",
    lieferant: parteiVoll("SellerTradeParty"),
    kunde: parteiVoll("BuyerTradeParty"),
    gruppen: gruppenPruefen(roh, hinweise),
    gutschrift: text(kind(dok, "TypeCode")) === "381",
    hinweise,
  });
};

const efactuur = (wurzel: Element): ERechnung =>
  abschliessen({
    format: "EFactuur",
    nummer: text(kind(wurzel, "Factuurnummer")),
    datum: isoDatum(text(kind(wurzel, "FactuurDatum"))),
    brutto: cents(kind(wurzel, "FactuurBedrag")),
    verkaeufer: "",
    kaeufer: "",
    lieferant: LEER,
    kunde: LEER,
    gruppen: [],
    gutschrift: false,
    hinweise: ["Kurzformat ohne Lieferant und Steuersatz"],
  });

export const xmlLesen = (inhalt: string): ERechnung | null => {
  const dok = new DOMParser().parseFromString(
    inhalt.replace(/^\uFEFF/, ""),
    "application/xml",
  );
  if (dok.getElementsByTagName("parsererror").length) return null;
  const wurzel = dok.documentElement;
  if (wurzel.localName === "Invoice" || wurzel.localName === "CreditNote")
    return ubl(wurzel);
  if (wurzel.localName === "CrossIndustryInvoice") return cii(wurzel);
  if (wurzel.localName === "EFactuur") return efactuur(wurzel);
  return null;
};

// ZUGFeRD/Factur-X: XML als eingebettete Datei im PDF, meist mit FlateDecode gepackt
export const pdfXmlFinden = async (
  roh: ArrayBuffer,
): Promise<string | null> => {
  const bytes = new Uint8Array(roh);
  const latin = new TextDecoder("latin1").decode(bytes);
  const muster = /\/Type\s*\/EmbeddedFile\b[\s\S]{0,600}?stream\r?\n/g;
  for (const treffer of latin.matchAll(muster)) {
    const start = (treffer.index ?? 0) + treffer[0].length;
    const ende = latin.indexOf("endstream", start);
    if (ende < 0) continue;
    const daten = bytes.slice(start, ende);
    const gepackt = /\/FlateDecode/.test(treffer[0]);
    try {
      const xml = gepackt
        ? await entpacken(daten)
        : new TextDecoder().decode(daten);
      if (/CrossIndustryInvoice|<(\w+:)?Invoice\b/.test(xml)) return xml;
    } catch {
      // kein lesbarer Anhang, weiter suchen
    }
  }
  return null;
};

const entpacken = async (daten: Uint8Array) => {
  const strom = new Blob([daten])
    .stream()
    .pipeThrough(new DecompressionStream("deflate"));
  const teile: Uint8Array[] = [];
  const leser = strom.getReader();
  // Nachlaufende Bytes vor "endstream" lassen den Strom am Ende scheitern; der gelesene Teil reicht
  try {
    for (;;) {
      const { done, value } = await leser.read();
      if (done) break;
      teile.push(value);
    }
  } catch {
    if (!teile.length) throw new Error("nicht entpackbar");
  }
  return new TextDecoder().decode(await new Blob(teile).arrayBuffer());
};

export const dateiLesen = async (datei: File): Promise<ERechnung | null> => {
  const name = datei.name.toLowerCase();
  if (name.endsWith(".xml")) return xmlLesen(await datei.text());
  if (name.endsWith(".pdf")) {
    const xml = await pdfXmlFinden(await datei.arrayBuffer());
    return xml ? xmlLesen(xml) : null;
  }
  return null;
};

// Vollständige Formate vor dem Kurzformat
export const besteAuslese = (liste: ERechnung[]) =>
  [...liste].sort(
    (a, b) => Number(a.format === "EFactuur") - Number(b.format === "EFactuur"),
  )[0] ?? null;

export const zusammenfuehren = (liste: ERechnung[]): ERechnung | null => {
  const beste = besteAuslese(liste);
  if (!beste) return null;
  const andere = liste.filter((r) => r !== beste);
  const brutto =
    beste.brutto ?? andere.find((r) => r.brutto !== null)?.brutto ?? null;
  const abweichend = andere.find(
    (r) => r.brutto !== null && brutto !== null && r.brutto !== brutto,
  );
  return {
    ...beste,
    nummer: beste.nummer ?? andere.find((r) => r.nummer)?.nummer ?? null,
    datum: beste.datum ?? andere.find((r) => r.datum)?.datum ?? null,
    brutto,
    hinweise: [
      ...beste.hinweise,
      ...(abweichend
        ? [
            `Dateien nennen unterschiedliche Beträge (${(abweichend.brutto ?? 0) / 100} €)`,
          ]
        : []),
    ],
  };
};
